// src/pages/steps/PhotoStep.tsx  (REFACTORED)
//
// CHANGES:
// - saveIfNeeded no longer uploads. It just crops the image and stores
//   the resulting File in profile.profilePhoto via onChange.
// - The upload happens in Register.tsx on the final step.
// - Removed useFileUpload from this component entirely.
// - isUploading / LoadingOverlay removed (upload happens elsewhere now).

import React, {
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Cropper, { Area } from 'react-easy-crop';
import {
  Field,
  CropContainer,
  ZoomWrapper,
  ZoomLabel,
  StandaloneFieldWrapper,
} from '../../styles/login.style';
import { ProfilePatch } from '../../hooks/auth.service';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { LinkedInHint } from '../../components/register/InfoBlock';
import { getCircularCroppedBlob as getCroppedBlob } from '../../utils/getCircularCroppedBlob';
import { compressImage } from '../../utils/compressImage';

interface Props {
  data: ProfilePatch;
  onChange: (d: ProfilePatch) => void;
  onValidityChange?: (ok: boolean) => void;
  isStandalone?: boolean;
}

export interface PhotoStepHandle {
  /** Crops the image and stores the File in state. Does NOT upload. */
  saveIfNeeded: () => Promise<File | null>;
}

export const PhotoStep = forwardRef<PhotoStepHandle, Props>(
  ({ data, onChange, onValidityChange, isStandalone }, ref) => {
    const [imageURL, setImageURL] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [pixelArea, setPixelArea] = useState<Area | null>(null);
    const [hasSaved, setHasSaved] = useState(false);

    const onCropComplete = useCallback(
      (_ignored: Area, areaPixels: Area) => setPixelArea(areaPixels),
      [],
    );

    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      setImageURL(url);
      if (!isStandalone) {
        onChange({ ...data, profilePhoto: file });
      }
    };

    // ── Crop only — no upload ───────────────────────────────────────
    const handleSave = async (): Promise<File | null> => {
      if (!imageURL || !pixelArea) return null;
      const blob = await getCroppedBlob(imageURL, pixelArea);
      const compressed = await compressImage(blob, 512, 0.8);
      const file = new File([compressed], 'avatar.webp', { type: 'image/webp' });
    
      onChange({ ...data, profilePhoto: file });
      setHasSaved(true);
      return file;
    };
    
    useImperativeHandle(ref, () => ({
      saveIfNeeded: async () => {
        if (imageURL && !hasSaved) return handleSave();
        return null;
      },
    }));

    useEffect(() => {
      onValidityChange?.(true);
    }, [onValidityChange]);

    const renderContent = (customCropCss?: any) => (
      <>
        <LinkedInHint
          css={{
            
              whiteSpace: 'normal',
              margin: '0 auto',
              maxWidth: '400px'
            
          }}
        />
        <CropContainer css={customCropCss}>
          {imageURL ? (
            <Cropper
              image={imageURL}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              minZoom={1}
              maxZoom={10}
              onZoomChange={setZoom}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              objectFit="cover"
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#888',
              }}
            >
              400×400px – click to upload
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0,
              cursor: 'pointer',
            }}
          />
        </CropContainer>
        <ZoomWrapper>
          <ZoomLabel>Zoom</ZoomLabel>
          <Slider
            min={1}
            max={10}
            step={0.05}
            value={zoom}
            style={{ width: '100%' }}
            onChange={(v) => {
              if (typeof v === 'number') setZoom(v);
            }}
          />
        </ZoomWrapper>
      </>
    );

    if (isStandalone) {
      return (
        <StandaloneFieldWrapper
          css={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {renderContent({
            flexGrow: 1,
            minHeight: 0,
            position: 'relative',
          })}
        </StandaloneFieldWrapper>
      );
    }

    return (
      <Field
        name="photo"
        css={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
      >
        {renderContent()}
      </Field>
    );
  },
);